import type { PayeeOrganizationDto } from "@fp/admin-api-client/types/payeeOrganizationDto.js";
import type { TalentMemberDto } from "@fp/admin-api-client/types/talentMemberDto.js";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ArrowRight, Building02, ChevronRight, SearchLg, User01 } from "@untitledui/icons";
import { useCallback, useMemo, useRef, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Input } from "@/components/base/input/input";
import { cx } from "@/utils/cx";
import { Drawer } from "../application/drawer/drawer";
import { TagSelector } from "../application/tag-selector/tag-selector";
import { AddButton } from "../base/buttons/add-button";
import {
  TalentMemberForm,
  TalentMemberFormActions,
  TalentMemberFormContent,
  TalentMemberFormErrorMessage,
} from "./talent-member-form";
import { EmploymentStatusFilters } from "./employment-status-filters";
import { PayeeOrganizationRow } from "./payee-organization-row";
import { PayoutReadinessFilters } from "./payout-readiness-filters";
import { payoutReadinessByStatus } from "./payout-readiness";
import { TalentMemberRow } from "./talent-member-row";
import { TalentPreviewCard } from "./talent-preview-card";

export type { AdminRosterFilterSettingsDto as RosterFilterSettings } from "@fp/admin-api-client/types/adminRosterFilterSettingsDto.js";

import type { AdminRosterFilterSettingsDto as RosterFilterSettings } from "@fp/admin-api-client/types/adminRosterFilterSettingsDto.js";

type RosterRow =
  | { kind: "talentMember"; id: string; talentMember: TalentMemberDto }
  | { kind: "payeeOrganization"; id: string; payeeOrganization: PayeeOrganizationDto };

export interface TalentMemberListProps {
  /** List of talentMembers to display */
  talentMembers: TalentMemberDto[];
  /** Payee organizations listed beside the talent members. Ignored when the list selects talent members. */
  payeeOrganizations?: PayeeOrganizationDto[];
  selectedTalentMemberIds?: Set<string>;
  /** Custom class name for the container */
  className?: string;
  onSelect?: (talentMemberIds: string[]) => void;
  onTalentMemberUpdate: () => void;
  onNewTalentMember: () => void;
  /** Optional navigation handler for when talent member is clicked. If provided, replaces drawer functionality */
  onTalentMemberNavigate?: (talentMemberId: string) => void;
  /** Navigation handler for when a payee organization is clicked */
  onPayeeOrganizationNavigate?: (payeeOrganizationId: string) => void;
  /** Initial filter settings loaded from server */
  initialFilterSettings?: RosterFilterSettings;
  /** Callback when filter settings change */
  onFilterSettingsChange?: (settings: RosterFilterSettings) => void;
}

export const TalentMemberList = ({
  talentMembers,
  payeeOrganizations,
  className,
  selectedTalentMemberIds,
  onSelect,
  onTalentMemberUpdate,
  onNewTalentMember,
  onTalentMemberNavigate,
  onPayeeOrganizationNavigate,
  initialFilterSettings,
  onFilterSettingsChange,
}: TalentMemberListProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [show1099, setShow1099] = useState(
    initialFilterSettings?.show1099 ?? true,
  );
  const [showW2, setShowW2] = useState(initialFilterSettings?.showW2 ?? true);
  const [showNA, setShowNA] = useState(
    initialFilterSettings?.showUnclassified ?? true,
  );
  const [showPayoutReady, setShowPayoutReady] = useState(
    initialFilterSettings?.showPayoutReady ?? true,
  );
  const [showPayoutNotReady, setShowPayoutNotReady] = useState(
    initialFilterSettings?.showPayoutNotReady ?? true,
  );
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isTalentMemberDrawerOpen, setIsTalentMemberDrawerOpen] = useState(false);
  const [editingTalentMemberId, setEditingTalentMemberId] = useState<string | null>(null);
  const allowSelect = Boolean(onSelect);
  const listRef = useRef<HTMLDivElement>(null);
  const [previewTalentMemberId, setPreviewTalentMemberId] = useState<string | null>(null);
  const [previewPosition, setPreviewPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [isPreviewPinned, setIsPreviewPinned] = useState(false);

  const handleFilterSettingsChange = useCallback(
    (partial: Partial<RosterFilterSettings>) => {
      const fullSettings: RosterFilterSettings = {
        show1099,
        showW2,
        showUnclassified: showNA,
        showPayoutReady,
        showPayoutNotReady,
        ...partial,
      };
      onFilterSettingsChange?.(fullSettings);
    },
    [
      show1099,
      showW2,
      showNA,
      showPayoutReady,
      showPayoutNotReady,
      onFilterSettingsChange,
    ],
  );

  const previewTalentMember = useMemo(() => {
    if (!previewTalentMemberId) return null;
    return talentMembers.find((c) => c.id === previewTalentMemberId) || null;
  }, [talentMembers, previewTalentMemberId]);

  const editingTalentMember = useMemo(() => {
    if (!editingTalentMemberId) return null;
    return talentMembers.find((c) => c.id === editingTalentMemberId) || null;
  }, [talentMembers, editingTalentMemberId]);

  const filteredAllTalentMembers = useMemo(() => {
    return talentMembers.filter((tm) => {
      const matchesSearch =
        searchQuery === "" ||
        tm.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tm.lastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tm.phone?.includes(searchQuery) ||
        tm.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        false;
      const matchesTags =
        selectedTags.length === 0 ||
        tm.tags?.some((tag) => selectedTags.includes(tag.id));
      return matchesSearch && matchesTags;
    });
  }, [talentMembers, searchQuery, selectedTags]);

  const typeFilteredTalentMembers = useMemo(() => {
    return filteredAllTalentMembers.filter(
      (tm) =>
        (showNA && (!tm.type || tm.type === "unclassified")) ||
        (show1099 && tm.type === "1099") ||
        (showW2 && tm.type === "w2"),
    );
  }, [filteredAllTalentMembers, show1099, showW2, showNA]);

  const filteredTalentMembers = useMemo(() => {
    return typeFilteredTalentMembers.filter((tm) => {
      const readiness = payoutReadinessByStatus[tm.payoutSetupStatus];
      if (readiness === "ready") return showPayoutReady;
      return showPayoutNotReady;
    });
  }, [
    typeFilteredTalentMembers,
    showPayoutReady,
    showPayoutNotReady,
  ]);

  const filteredPayeeOrganizations = useMemo(() => {
    if (allowSelect || !payeeOrganizations || selectedTags.length > 0) return [];
    return payeeOrganizations
      .filter(
        (payeeOrganization) =>
          searchQuery === "" ||
          payeeOrganization.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      .sort((left, right) => left.name.localeCompare(right.name));
  }, [allowSelect, payeeOrganizations, searchQuery, selectedTags]);

  const rows = useMemo<RosterRow[]>(
    () => [
      ...filteredTalentMembers.map<RosterRow>((talentMember) => ({ kind: "talentMember", id: talentMember.id, talentMember })),
      ...filteredPayeeOrganizations.map<RosterRow>((payeeOrganization) => ({ kind: "payeeOrganization", id: payeeOrganization.id, payeeOrganization })),
    ],
    [filteredTalentMembers, filteredPayeeOrganizations],
  );

  const allSelected = filteredTalentMembers.every((tm) =>
    selectedTalentMemberIds?.has(tm.id),
  );

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 72,
    overscan: 5,
    getItemKey: (index) => rows[index].id,
  });

  const handleSelect = (talentMemberId: string) => {
    if (!onSelect || !selectedTalentMemberIds) return;
    const newSet = new Set(selectedTalentMemberIds);
    if (newSet.has(talentMemberId)) {
      newSet.delete(talentMemberId);
    } else {
      newSet.add(talentMemberId);
    }
    onSelect(Array.from(newSet));
  };

  const handleSelectAll = () => {
    if (!onSelect || !selectedTalentMemberIds) return;
    const allCurrentIds = filteredTalentMembers.map((c) => c.id);

    const newSet = new Set(selectedTalentMemberIds);
    if (allSelected) {
      for (const id of allCurrentIds) {
        newSet.delete(id);
      }
      onSelect(Array.from(newSet));
    } else {
      for (const id of allCurrentIds) {
        newSet.add(id);
      }
      onSelect(Array.from(newSet));
    }
  };

  const handleTalentMemberSuccess = async () => {
    setEditingTalentMemberId(null);
    setIsTalentMemberDrawerOpen(false);
    onTalentMemberUpdate();
  };

  const rowStyle = (virtualItem: { size: number; start: number }) => ({
    position: "absolute" as const,
    top: 0,
    left: 0,
    width: "100%",
    height: `${virtualItem.size}px`,
    transform: `translateY(${virtualItem.start}px)`,
  });

  const rowClassName =
    "flex items-center justify-between px-5 py-3 border-b border-secondary last:border-b-0 gap-3 w-full text-left bg-transparent cursor-pointer";

  return (
    <div className={cx("relative flex flex-col min-h-0 bg-primary", className)}>
      {/* Search and Controls */}
      <div className="bg-gray-50 border-y border-secondary">
        {/* Search Input */}
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <Input
              value={searchQuery}
              onChange={setSearchQuery}
              icon={SearchLg}
              className="flex-1"
              placeholder={payeeOrganizations ? "Search payees" : "Search talent members"}
            />
            <AddButton onClick={onNewTalentMember}>New</AddButton>
          </div>
        </div>

        {/* Employment Status Filters */}
        <div className="px-5 pb-3">
          <EmploymentStatusFilters
            show1099={show1099}
            showW2={showW2}
            showUnclassified={showNA}
            onShow1099Change={setShow1099}
            onShowW2Change={setShowW2}
            onShowUnclassifiedChange={setShowNA}
            onFilterSettingsChange={handleFilterSettingsChange}
            onOpenTagFilter={() => setIsFilterDrawerOpen(true)}
            selectedTagCount={selectedTags.length}
          />
        </div>

        {/* Payment Readiness Filters */}
        <div className="px-5 pb-5">
          <PayoutReadinessFilters
            showPayoutReady={showPayoutReady}
            showPayoutNotReady={showPayoutNotReady}
            onShowPayoutReadyChange={setShowPayoutReady}
            onShowPayoutNotReadyChange={setShowPayoutNotReady}
            onFilterSettingsChange={handleFilterSettingsChange}
          />
        </div>
      </div>

      {allowSelect && (
        <div className="flex items-center py-3 px-5 border-b-2 border-secondary bg-secondary">
          <Checkbox
            size="md"
            isSelected={allSelected}
            onChange={handleSelectAll}
            className="mr-3"
          />
          <button
            type="button"
            onClick={handleSelectAll}
            className="text-sm text-brand hover:text-brand_hover font-semibold cursor-pointer"
          >
            {allSelected ? "Deselect All" : "Select All"}
          </button>
        </div>
      )}

      {!allowSelect && selectedTags.length > 0 && (payeeOrganizations?.length ?? 0) > 0 && (
        <p className="px-5 py-2 text-xs text-tertiary border-b border-secondary">
          Organizations carry no tags and are hidden while a tag filter is active
        </p>
      )}

      {/* Roster List */}
      <div
        ref={listRef}
        className="flex flex-col flex-1 min-h-0 h-full overflow-auto"
      >
        <div className="flex-1">
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualItem) => {
              const row = rows[virtualItem.index];

              if (row.kind === "payeeOrganization") {
                const handleOrganizationRowClick = () => onPayeeOrganizationNavigate?.(row.payeeOrganization.id);
                return (
                  // biome-ignore lint/a11y/useSemanticElements: mirrors the talent member row so both kinds lay out identically
                  <div
                    key={virtualItem.key}
                    role="button"
                    tabIndex={0}
                    style={rowStyle(virtualItem)}
                    className={rowClassName}
                    onClick={handleOrganizationRowClick}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleOrganizationRowClick();
                      }
                    }}
                  >
                    <div className="relative mr-3">
                      <div className="p-2 rounded-full bg-gray-100">
                        <Building02 className="size-5 text-tertiary" />
                      </div>
                    </div>
                    <PayeeOrganizationRow payeeOrganization={row.payeeOrganization} actions={<ChevronRight />} />
                  </div>
                );
              }

              const talentMember = row.talentMember;
              const isSelected = selectedTalentMemberIds?.has(talentMember.id);
              const handleRowClick = () => {
                if (allowSelect) {
                  handleSelect(talentMember.id);
                } else if (onTalentMemberNavigate) {
                  onTalentMemberNavigate(talentMember.id);
                } else {
                  setEditingTalentMemberId(talentMember.id);
                  setIsTalentMemberDrawerOpen(true);
                }
              };
              return (
                // biome-ignore lint/a11y/useSemanticElements: row contains a nested <button> (avatar preview) so the row itself cannot be a <button> without invalid HTML
                <div
                  key={virtualItem.key}
                  role="button"
                  tabIndex={0}
                  style={rowStyle(virtualItem)}
                  className={rowClassName}
                  onClick={handleRowClick}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleRowClick();
                    }
                  }}
                >
                  {onSelect && (
                    <div onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="md"
                        isSelected={isSelected}
                        onChange={() => handleSelect(talentMember.id)}
                        className="mr-3"
                      />
                    </div>
                  )}
                  <div className="relative mr-3">
                    <button
                      type="button"
                      className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (
                          previewTalentMemberId === talentMember.id &&
                          isPreviewPinned
                        ) {
                          setPreviewTalentMemberId(null);
                          setPreviewPosition(null);
                          setIsPreviewPinned(false);
                        } else {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setPreviewPosition({
                            top: rect.bottom + 8,
                            left: rect.left,
                          });
                          setPreviewTalentMemberId(talentMember.id);
                          setIsPreviewPinned(true);
                        }
                      }}
                      onMouseEnter={(e) => {
                        if (isPreviewPinned) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        setPreviewPosition({
                          top: rect.bottom + 8,
                          left: rect.left,
                        });
                        setPreviewTalentMemberId(talentMember.id);
                      }}
                      onMouseLeave={() => {
                        if (isPreviewPinned) return;
                        setPreviewTalentMemberId(null);
                        setPreviewPosition(null);
                      }}
                    >
                      <User01 className="size-5 text-tertiary" />
                    </button>
                  </div>
                  <TalentMemberRow
                    talent={talentMember}
                    actions={allowSelect ? undefined : <ChevronRight />}
                  />
                </div>
              );
            })}
          </div>

          {rows.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 py-8">
              <p className="text-tertiary text-sm">{payeeOrganizations ? "No payees found" : "No talent members found"}</p>
              <button
                type="button"
                onClick={onNewTalentMember}
                className="inline-flex items-center gap-1 text-sm text-brand hover:text-brand_hover font-semibold cursor-pointer underline underline-offset-2"
              >
                {payeeOrganizations ? "Add a payee" : "Invite a talent member"}
                <ArrowRight className="size-4" />
              </button>
            </div>
          )}
        </div>
      </div>
      {previewTalentMember && previewPosition && (
        <>
          {isPreviewPinned && (
            <button
              type="button"
              aria-label="Close preview"
              className="fixed inset-0 z-40 cursor-default"
              onClick={(e) => {
                e.stopPropagation();
                setPreviewTalentMemberId(null);
                setPreviewPosition(null);
                setIsPreviewPinned(false);
              }}
            />
          )}
          <div
            role="tooltip"
            className="fixed z-50"
            style={{ top: previewPosition.top, left: previewPosition.left }}
            onMouseEnter={() => {
              if (!isPreviewPinned) setPreviewTalentMemberId(previewTalentMember.id);
            }}
            onMouseLeave={() => {
              if (isPreviewPinned) return;
              setPreviewTalentMemberId(null);
              setPreviewPosition(null);
            }}
          >
            <TalentPreviewCard talent={previewTalentMember} />
          </div>
        </>
      )}
      <Drawer
        isOpen={isFilterDrawerOpen}
        onClose={setIsFilterDrawerOpen}
        title="Filter by tag"
      >
        <TagSelector selectedTags={selectedTags} onChange={setSelectedTags} />

        <Button
          className="mt-6 w-full"
          size="lg"
          onClick={() => {
            setIsFilterDrawerOpen(false);
          }}
        >
          Done
        </Button>
      </Drawer>

      <Drawer
        isOpen={isTalentMemberDrawerOpen}
        onClose={setIsTalentMemberDrawerOpen}
        title="Edit Talent Member"
      >
        {editingTalentMember && (
          <TalentMemberForm talentMember={editingTalentMember} onSuccess={handleTalentMemberSuccess}>
            <TalentMemberFormContent />
            <TalentMemberFormErrorMessage />
            <TalentMemberFormActions />
          </TalentMemberForm>
        )}
      </Drawer>
    </div>
  );
};

TalentMemberList.displayName = "TalentMemberList";
